/**
 * useSalesFunnelData Hook
 *
 * Fetches the per-salesperson Sales Funnel (past/next 5 working days) from
 * GET /sales-funnel for the Forecast page's Sales Department Analysis card.
 * Fetching is gated on `enabled` so the table loads lazily when its section
 * is first expanded.
 */

import { useState, useEffect, useCallback } from 'react';
import { getSalesFunnel, SalesFunnelResponse } from '../services/api';

interface UseSalesFunnelDataParams {
  locationId: number;
  enabled: boolean;
}

interface UseSalesFunnelDataResult {
  data: SalesFunnelResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useSalesFunnelData({
  locationId,
  enabled,
}: UseSalesFunnelDataParams): UseSalesFunnelDataResult {
  const [data, setData] = useState<SalesFunnelResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getSalesFunnel(locationId);
      if (res.success && res.data) {
        setData(res.data);
      } else {
        setError(res.error || 'Failed to load sales funnel data');
        setData(null);
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  useEffect(() => {
    if (!enabled || locationId === 0) {
      setData(null);
      setError(null);
      return;
    }
    fetchData();
  }, [enabled, locationId, fetchData]);

  return { data, loading, error, refetch: fetchData };
}
